import React, { Component } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'redux';

import RenterOnboardingMutation from 'queries/mutations/RenterOnboardingMutation.gql';
import getTrackingCookie from 'util/adwords';
import { getCookie } from 'util/cookies';
import withUser from 'components/withUser';

import SpaceCalculatorPageView from './SpaceCalculatorPageView';
import ROOM_TYPES from './room-types';
import steps from './steps';

const cities = [
  'New York',
  'Atlanta',
  'Austin',
  'Los Angeles',
  'Dallas',
  'Houston',
  'Phoenix',
  'Indianapolis',
  'Washington',
];

class SpaceCalculatorPageContainer extends Component {
  state = {
    currentStep: this.props.currentUser
      ? steps.chartDisplay
      : steps.introduction,
    numEmployees: 15,
    spacePerEmployee: 150,
    userDefinedRooms: {},
    hoveredSlice: -1,
    selectedCity: 'New York',
  };

  getRecommendations(numEmployees) {
    const recommendations = [];
    ROOM_TYPES.forEach(({ name, size, estimate, color }) => {
      const count = estimate(numEmployees);
      recommendations.push({
        name,
        count,
        size,
        color,
        totalSize: size * count,
      });
    });
    return recommendations;
  }

  updateRenterOnboarding = (updatedUserPayload) => {
    const onboardingData = {
      email: updatedUserPayload.email,
      numberOfEmployees: this.state.numEmployees,
      squareFeet: this.totalSquareFeet(),
      gclid: getTrackingCookie(),
      signUpType: 'Space Calculator',
      googleAnalyticsId: getCookie('_gid'),
    };

    this.props.updateRenterOnboarding({ variables: onboardingData });
  };

  totalSquareFeet() {
    // add generic work space to sum of room sizes
    const workSpace = this.state.numEmployees * this.state.spacePerEmployee;
    const rooms = this.rooms();
    return (
      workSpace + rooms.reduce((prev, { totalSize }) => prev + totalSize, 0)
    );
  }

  rooms() {
    // merge recommended rooms with user-defined values
    const definedRoomTypes = [];
    return this.getRecommendations(this.state.numEmployees)
      .map((recommendation) => {
        // use user-set quantity if exists, otherwise use recommendation
        const count = this.state.userDefinedRooms[recommendation.name];
        if (typeof count !== 'undefined') {
          definedRoomTypes.push(recommendation.name);
          return {
            ...recommendation,
            count,
            totalSize: count * recommendation.size,
          };
        }
        return recommendation;
      })
      .filter(
        (recommendation) =>
          recommendation.totalSize > 0 ||
          typeof this.state.userDefinedRooms[recommendation.name] !==
            'undefined'
      );
  }

  onChange = (evt) => {
    this.setState({ [evt.target.name]: Number(evt.target.value) });
  };

  onUpdateRoom = ({ name, value }) => {
    this.setState({
      userDefinedRooms: {
        ...this.state.userDefinedRooms,
        [name]: Math.max(0, value),
      },
    });
  };

  onUpdateStep = (newStep) => {
    this.setState({ currentStep: newStep });
  };

  onAddRoom = (evt) => {
    const roomName = evt.target.value;
    // start from existing user defined value if exists
    let existingValue = this.state.userDefinedRooms[roomName];
    if (typeof existingValue === 'undefined') {
      // start from existing recommendation if exists
      const recommendation = this.getRecommendations(
        this.state.numEmployees
      ).filter((t) => t.name === roomName)[0];
      if (recommendation) {
        existingValue = recommendation.count;
      }
    }
    // if no other value exists, just use 1
    this.onUpdateRoom({ name: roomName, value: 1 + (existingValue || 0) });
  };

  onHoverSlice = (evt) => {
    let index = -1;
    if (evt && evt.target) {
      for (let i = 0; i < evt.target.parentNode.childNodes.length; i++) {
        if (evt.target.parentNode.childNodes[i] === evt.target) {
          index = i;
          break;
        }
      }
    }
    this.setState({
      hoveredSlice: index,
    });
  };

  onCityChange = (evt) => {
    this.setState({
      selectedCity: evt.target.value,
    });
  };

  render() {
    const rooms = this.rooms();
    const workSpace = this.state.numEmployees * this.state.spacePerEmployee;
    const totalSqft = this.totalSquareFeet();

    // prepare size for chart display
    const chartSlices = [
      { name: 'Work Space', value: workSpace, color: '#004f8a' },
      ...rooms
        .map((room) => ({
          name: room.name,
          value: room.totalSize,
          color: room.color,
          count: room.count,
        }))
        .filter((t) => t.value > 0),
    ];

    return (
      <SpaceCalculatorPageView
        currentUser={this.props.currentUser}
        numEmployees={this.state.numEmployees}
        spacePerEmployee={this.state.spacePerEmployee}
        cities={cities}
        currentStep={this.state.currentStep}
        selectedCity={this.state.selectedCity}
        rooms={rooms}
        roomTypes={ROOM_TYPES}
        totalSqft={totalSqft}
        chartSlices={chartSlices}
        onChange={this.onChange}
        onUpdateStep={this.onUpdateStep}
        onAddRoom={this.onAddRoom}
        onUpdateRoom={this.onUpdateRoom}
        selectedSlice={this.state.hoveredSlice}
        onHoverSlice={this.onHoverSlice}
        onCityChange={this.onCityChange}
        updateRenterOnboarding={this.updateRenterOnboarding}
      />
    );
  }
}

export default compose(
  withUser,
  graphql(RenterOnboardingMutation, { name: 'updateRenterOnboarding' })
)(SpaceCalculatorPageContainer);
